import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
} from '@angular/core';

import { environment } from 'src/environments/environment';

import { AnalyticsFactory } from 'src/app/ajs-upgraded-providers';
import { ModalService } from 'src/app/components/modals/modal.service';

import { AttributeDataService } from '../../services/attribute-data.service';
import { GoogleCalendarService } from '../services/google-calendar.service';
import { MicrosoftOutlookService } from '../services/microsoft-outlook.service';
import { ComponentsService } from '../../services/components.service';
import { OAuthService } from 'src/app/components/content-oauth/services/oauth.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';

import { CalendarBase } from '../calendar-base/calendar.base';

@Component({
  selector: 'template-component-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false,
})
export class CalendarComponent extends CalendarBase {
  override multiCalendarComponent = true;

  public componentId: string;
  public descriptionIsVisible: boolean;
  public locationIsVisible: boolean;

  calendarPickerGoogleOpen = false;
  calendarPickerOutlookOpen = false;

  constructor(
    private elementRef: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    modalService: ModalService,
    private analyticsFactory: AnalyticsFactory,
    private attributeDataService: AttributeDataService,
    private componentsFactory: ComponentsService,
    templateEditorService: TemplateEditorService,
    userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    googleCalendarService: GoogleCalendarService,
    microsoftOutlookService: MicrosoftOutlookService,
    authService: OAuthService,
  ) {
    super(
      changeDetectorRef,
      googleCalendarService,
      microsoftOutlookService,
      authService,
      userStateService,
      modalService,
      templateEditorService,
    );
    this.componentsFactory.registerDirective({
      type: 'rise-data-calendar',
      element: this.elementRef.nativeElement,
      show: async () => {
        this.spinner = false;
        this.componentId = this.componentsFactory.selected.id;
        this.load();
        this.userAccount = authService.getUserIdentifier(null);
        if (this.isPublicUrl()) {
          this.validateCalendarUrl();
        } else {
          await this.validateCredentials();
        }
      },
    });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (this._directiveIsVisible() && !document.hidden && this.isPublicUrl()) {
      void this.validateCalendarUrl();
    }
  }

  private _directiveIsVisible() {
    // This directive is instantiated once by templateAttributeEditor
    // It becomes visible when <rise-data-calendar> is selected
    return (
      this.componentsFactory.selected &&
      this.componentsFactory.selected.type === 'rise-data-calendar'
    );
  }

  load() {
    this.reset();

    this.source = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'source',
    );
    this.range =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'range',
      ) ?? this.DEFAULT_RANGE;
    this.period =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'period',
      ) ?? this.DEFAULT_PERIOD;
    const hideCompleted =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'completed',
      ) ?? false; // default differs from layout-EMS
    this.showCompleted = !hideCompleted;

    this.componentAccount = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'account',
    );
    this.selectedName =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'name',
      ) || '';
    this.provider = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'provider',
    );
    this.outlookGroupId = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'groupId',
    );

    if (this.outlookGroupId) {
      this.selectedOutlookGroup = this.outlookGroupId || '';
      this.selectedOutlookGroupName =
        this.attributeDataService.getAvailableAttributeData(
          this.componentId,
          'groupName',
        ) || '';
    } else {
      this.selectedOutlookGroup = '';
      this.selectedOutlookGroupName = '';
    }

    const storedCalendarType =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'calendarType',
      );

    const rawCalendars = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'calendars',
    );

    if (rawCalendars != null) {
      this.selectedCalendars = this.parseCalendarsAttribute(rawCalendars);
    } else if (this.selectedName && this.source) {
      // legacy attribute data that doesn't have calendars attribute
      this.selectedCalendars = [
        {
          calendarId: this.source,
          calendarName: this.selectedName,
          groupId: this.outlookGroupId || undefined,
          groupName: this.selectedOutlookGroupName || undefined,
        },
      ];
    } else {
      this.selectedCalendars = [];
    }

    const featuresString =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'features',
      ) ?? '';
    const features = featuresString
      .split(',')
      .map((feature) => feature.trim().toLowerCase());
    this.descriptionIsVisible = features.includes('description');
    this.locationIsVisible = features.includes('location');
    this.showDescription =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'description',
      ) === 'false'
        ? false
        : true;
    this.showLocation =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'location',
      ) === 'false'
        ? false
        : true;

    if (storedCalendarType) {
      this.calendarType = storedCalendarType;
    } else {
      // calendarType can be empty for two reasons:
      // 1) component is not configured yet
      // 2) it's a legacy attribute data that doesn't have calendarType attribute

      if (this.source) {
        // source exists, so it's a legacy attribute data

        if (this.isLegacyPublicUrl()) {
          this.calendarType = this.CALENDAR_TYPE_PUBLIC;
          // New Public URL UI no longer requires showing username of the authenticated user
          this.componentAccount = null;
        } else {
          this.calendarType = this.provider;
        }
      }
    }

    this.updateValidRange();
  }

  onGoogleCalendarDropdownOpenChange(open: boolean): void {
    this.calendarPickerGoogleOpen = open;
    if (open) {
      this.calendarPickerOutlookOpen = false;
    }
  }

  onOutlookCalendarDropdownOpenChange(open: boolean): void {
    this.calendarPickerOutlookOpen = open;
    if (open) {
      this.calendarPickerGoogleOpen = false;
    }
  }

  async validateCalendarUrl(): Promise<boolean> {
    if (!this.source) {
      return true;
    }

    this.spinner = true;
    let isValid = false;

    try {
      const provider = this.getProviderFromUrl(this.source);
      switch (provider) {
        case this.PROVIDER_GOOGLE_CALENDAR: {
          const result = await this.googleCalendarService.validate(this.source);
          this.calendarUrlStatus = result; // preserves 'VALID' | 'INVALID' | 'FAILED'
          isValid = result === 'VALID';
          break;
        }
        case this.PROVIDER_ICAL:
          isValid = this.validateIcalUrl();
          this.calendarUrlStatus = isValid ? 'VALID' : 'INVALID';
          break;
        default:
          isValid = false;
          this.calendarUrlStatus = 'INVALID';
      }

      if (isValid) {
        this.provider = provider;
      }
    } finally {
      this.spinner = false;
    }

    this.changeDetectorRef.detectChanges();

    return isValid;
  }

  protected override save() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'calendarType',
      this.calendarType,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'provider',
      this.provider,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'account',
      this.componentAccount,
    );

    const isInvalidPublicUrl =
      this.isPublicUrl() && this.source && this.calendarUrlStatus !== 'VALID';

    // save source only if public url
    this.attributeDataService.setAttributeData(
      this.componentId,
      'source',
      this.isPublicUrl() && !isInvalidPublicUrl ? this.source : '',
    );

    // clear previously saved name, groupId, and groupName
    // because they are now saved in the "calendars" attribute
    this.attributeDataService.setAttributeData(this.componentId, 'name', '');
    this.attributeDataService.setAttributeData(this.componentId, 'groupId', '');
    this.attributeDataService.setAttributeData(
      this.componentId,
      'groupName',
      '',
    );

    this.attributeDataService.setAttributeData(
      this.componentId,
      'calendars',
      this.selectedCalendars,
    );

    if (this.isValidRange()) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'range',
        this.range,
      );
      this.attributeDataService.setAttributeData(
        this.componentId,
        'period',
        this.period,
      );
    }

    this.attributeDataService.setAttributeData(
      this.componentId,
      'completed',
      !this.showCompleted,
    );

    if (this.descriptionIsVisible) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'description',
        this.showDescription ? 'true' : 'false',
      );
    }
    if (this.locationIsVisible) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'location',
        this.showLocation ? 'true' : 'false',
      );
    }
  }

  protected override onOutlookSingleCalendarAutoSelected(): void {}

  override reset() {
    super.reset();
    this.showCompleted = true;

    this.updateValidRange();
  }

  async selectCalendarType(calendarType: string) {
    this.reset();

    this.calendarType = calendarType;

    if (calendarType !== this.CALENDAR_TYPE_PUBLIC) {
      this.provider = calendarType;
      // Note, for public URL provider is set during URL validation.
    }

    if (this.USER_AUTHORIZED_PROVIDERS.includes(calendarType)) {
      await this.connectAccount();
      if (this.authenticateFailed || !this.componentAccount) {
        this.calendarType = null;
        this.provider = null;
      }
    }

    this.save();
    this.changeDetectorRef.detectChanges();
  }

  isLegacyPublicUrl() {
    return (
      this.provider !== this.PROVIDER_OUTLOOK_CALENDAR && !this.selectedName
    );
  }

  get revokeFailedProviderName(): string {
    switch (this.revokeFailedProvider) {
      case this.PROVIDER_GOOGLE_CALENDAR:
        return 'Google';
      case this.PROVIDER_OUTLOOK_CALENDAR:
        return 'Microsoft';
      default:
        return '';
    }
  }

  _getSourceTypeName(provider) {
    switch (provider) {
      case this.PROVIDER_GOOGLE_CALENDAR:
        if (this.isPublicUrl()) {
          return 'Public';
        }

        return 'Google';
      case this.PROVIDER_OUTLOOK_CALENDAR:
        return 'Microsoft Outlook';
      default:
        return 'Public';
    }
  }

  override track() {
    this.analyticsFactory.track('Calendar Component Updated', {
      companyId: this.companyStateService.getSelectedCompanyId(),
      presentationId: this.templateEditorService.presentation.id,
      presentationName: this.templateEditorService.presentation.name,
      componentId: this.componentId,
      sourceType: this._getSourceTypeName(this.provider),
    });
  }
}
